// ABOUTME: Trade and tradesperson management functions backed by the Worker API.
// ABOUTME: Replaces Supabase queries with fetch calls, preserving all export signatures.

import { api } from "@/lib/supabase/client";
import { Tradesperson, UserTradesperson } from "@/lib/types";
import { debug } from "@/lib/utils";
import { handleApiError, withErrorHandling } from "@/lib/errors";
import { detectCountry, coarsenPostcode, getPostcodeFallbacks } from "@/lib/postcodeUtils";

type TradespersonInsert = {
  org: string; contact: string; phone?: string; email?: string;
  website?: string; notes?: string; category?: string; trade_type?: string;
  tags?: string[]; postcode?: string | string[];
};

export const getUserTradespeople = withErrorHandling(
  async (_userId: string) => {
    const { user_tradespeople } = await api.get<{ user_tradespeople: UserTradesperson[] }>('/user-tradespeople');
    return user_tradespeople || [];
  },
  [],
  'Failed to load tradespeople'
);

export async function addUserTradesperson(userId: string, tradespersonId: string, data: {
  notes?: string; preferred?: boolean;
}) {
  try {
    const { user_tradesperson } = await api.post<{ user_tradesperson: UserTradesperson | null }>('/user-tradespeople', {
      tradesperson_id: tradespersonId, ...data,
    });
    return user_tradesperson ? [user_tradesperson] : [];
  } catch (error) {
    handleApiError(error, 'Failed to add tradesperson');
    throw error;
  }
}

export async function updateUserTradesperson(id: string, data: {
  notes?: string; preferred?: boolean;
}) {
  try {
    await api.put(`/user-tradespeople/${id}`, data);
    return [{ id, ...data }];
  } catch (error) {
    handleApiError(error, 'Failed to update tradesperson');
    throw error;
  }
}

export async function deleteUserTradesperson(id: string) {
  try {
    await api.del(`/user-tradespeople/${id}`);
    return null;
  } catch (error) {
    handleApiError(error, 'Failed to remove tradesperson');
    throw error;
  }
}

export const searchGlobalTradespeople = withErrorHandling(
  async (keyword: string, tradeTypes?: string[]) => {
    const params = new URLSearchParams();
    if (keyword) params.set('keyword', keyword);
    if (tradeTypes?.length) params.set('types', tradeTypes.join(','));
    const { tradespeople } = await api.get<{ tradespeople: Tradesperson[] }>(`/tradespeople/search?${params}`);
    return tradespeople || [];
  },
  [],
  'Failed to search for tradespeople'
);

export async function insertTradesperson(data: TradespersonInsert) {
  try {
    const { tradesperson } = await api.post<{ tradesperson: Tradesperson }>('/tradespeople', data);
    return { data: tradesperson ? [tradesperson] : [] };
  } catch (error) {
    handleApiError(error, 'Failed to create tradesperson');
    throw error;
  }
}

export async function updateTradespersonVerification(id: string, verified: boolean) {
  try {
    await api.put(`/tradespeople/${id}/verify`, { verified });
    return [{ id, verified }] as unknown as Tradesperson[];
  } catch (error) {
    handleApiError(error, 'Failed to update verification status');
    throw error;
  }
}

export const getLocalTradespeople = withErrorHandling(
  async (categoryOrType: string, fieldName: 'category' | 'trade_type' = 'category') => {
    const params = new URLSearchParams({ keyword: categoryOrType });
    if (fieldName === 'trade_type') params.set('types', categoryOrType);
    const { tradespeople } = await api.get<{ tradespeople: Tradesperson[] }>(`/tradespeople/search?${params}`);
    return tradespeople || [];
  },
  [],
  'Failed to load tradespeople'
);

export const getPostcodeTradespeople = withErrorHandling(
  async (inputPostcode: string, limit = 10, usePrefixFallback = false) => {
    const coarsened = coarsenPostcode(inputPostcode).toUpperCase();
    debug.log('Getting tradespeople for postcode:', { input: inputPostcode, coarsened, usePrefixFallback });

    let { tradespeople } = await api.get<{ tradespeople: Tradesperson[] }>(
      `/tradespeople/postcode/${encodeURIComponent(coarsened)}?limit=${limit}`
    );

    if ((!tradespeople || tradespeople.length === 0) && usePrefixFallback) {
      const country = detectCountry(inputPostcode);
      const fallbacks = getPostcodeFallbacks(inputPostcode, country);
      for (const fb of fallbacks) {
        const res = await api.get<{ tradespeople: Tradesperson[] }>(
          `/tradespeople/postcode/${encodeURIComponent(fb.toUpperCase())}?limit=${limit}`
        );
        if (res.tradespeople?.length) { tradespeople = res.tradespeople; break; }
      }
    }

    return (tradespeople || []).map(t => ({ ...t, recommendation_count: t.recommendation_count || 0 }));
  },
  [],
  'Failed to load tradespeople for area'
);

export const getRecentTradespeople = withErrorHandling(
  async (_userId: string, limit = 3) => {
    const { user_tradespeople } = await api.get<{ user_tradespeople: Array<{ tradesperson: Tradesperson | null }> }>('/user-tradespeople');
    return (user_tradespeople || []).slice(0, limit).map((item) => item.tradesperson).filter(Boolean);
  },
  [],
  'Failed to load recent tradespeople'
);

export const getUserSharedTradespeople = withErrorHandling(
  async (_userId: string) => {
    const { user_tradespeople } = await api.get<{ user_tradespeople: Array<{ tradesperson: Tradesperson | null }> }>('/user-tradespeople');
    return (user_tradespeople || []).map((item) => item.tradesperson).filter(Boolean);
  },
  [],
  'Failed to load user tradespeople'
);

export const getTradesPeopleByUsername = withErrorHandling(
  async (username: string) => {
    const data = await api.get<{ tradespeople: Tradesperson[]; userData: { id: string; postcode: string | null; location: string | null } | null }>(
      `/tradespeople/username/${encodeURIComponent(username)}`
    );
    return {
      tradespeople: data.tradespeople || [],
      userData: data.userData ? { ...data.userData, location: data.userData.postcode } : null,
    };
  },
  { tradespeople: [], userData: null },
  'Failed to load user tradespeople by username'
);

export const getTradesPeopleByUsernameWithCounts = withErrorHandling(
  async (username: string) => {
    return getTradesPeopleByUsername(username);
  },
  { tradespeople: [], userData: null },
  'Failed to load user tradespeople with save counts'
);

export const getPopularTradespeople = withErrorHandling(
  async (limit = 10) => {
    const { tradespeople } = await api.get<{ tradespeople: Tradesperson[] }>(`/tradespeople/popular?limit=${limit}`);
    return tradespeople || [];
  },
  [],
  'Failed to load popular tradespeople'
);

export const getPopularTradespeopleByCountry = withErrorHandling(
  async (userPostcode: string, limit = 10) => {
    const userCountry = detectCountry(userPostcode);
    if (!userCountry) return [];
    const { tradespeople } = await api.get<{ tradespeople: Tradesperson[] }>(`/tradespeople/popular?limit=${limit * 3}`);
    return (tradespeople || [])
      .filter(t => detectCountry((t as Tradesperson & { postcodes?: string[] }).postcodes?.[0]) === userCountry)
      .slice(0, limit);
  },
  [],
  'Failed to load popular tradespeople by country'
);

export const getTradesPeopleByPostcodeWithCounts = withErrorHandling(
  async (postcode: string, limit = 100) => {
    debug.log(`Searching for tradespeople by postcode: ${postcode}`);
    let tradespeople = await getPostcodeTradespeople(postcode, limit, false);
    if (!tradespeople?.length) {
      tradespeople = await getPostcodeTradespeople(postcode, limit, true);
    }
    return (tradespeople || []).map(t => ({ ...t, save_count: 0 }));
  },
  [],
  'Failed to load tradespeople with save counts'
);

export const searchTradeByContact = withErrorHandling(
  async (phone?: string, email?: string, category?: string) => {
    if (!phone && !email) return null;
    const params = new URLSearchParams();
    if (phone) params.set('phone', phone);
    if (email) params.set('email', email);
    if (category) params.set('category', category);
    const { tradesperson } = await api.get<{ tradesperson: Tradesperson | null }>(`/tradespeople/contact-search?${params}`);
    return tradesperson;
  },
  null,
  'Failed to search for tradesperson'
);

export const isTradesPersonSavedByUser = withErrorHandling(
  async (_userId: string, tradespersonId: string) => {
    const { saved } = await api.get<{ saved: boolean }>(`/user-tradespeople/check/${tradespersonId}`);
    return saved;
  },
  false,
  'Failed to check if tradesperson is saved'
);
